// Implements: F-UIX-021, F-VER-014, F-UIX-009, F-UIX-010
/**
 * The version tree of a mockup: the container that owns the query.
 *
 * The three states are components (F-UIX-010), as everywhere else. An empty
 * tree is not an error: a mockup nobody has generated for has no versions yet.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { VersionTree } from "../components/VersionTree";
import { useVersionTree } from "../hooks/useVersionTree";

export interface VersionTreeContainerProps {
  readonly mockupId: string;
}

export function VersionTreeContainer({ mockupId }: VersionTreeContainerProps): ReactElement {
  const { t } = useTranslation();
  const tree = useVersionTree(mockupId);

  return (
    <>
      {tree.status === "pending" ? <LoadingState label={t("common.state.loading")} /> : null}

      {tree.status === "error" ? (
        <ErrorState
          error={tree.error}
          onRetry={() => {
            tree.refetch();
          }}
        />
      ) : null}

      {tree.status === "success" && tree.data.nodes.length === 0 ? (
        <EmptyState title={t("versions.title")} body={t("versions.empty")} />
      ) : null}

      {tree.status === "success" && tree.data.nodes.length > 0 ? (
        <VersionTree
          mockupId={mockupId}
          nodes={tree.data.nodes}
          currentVersionId={tree.data.currentVersionId}
        />
      ) : null}
    </>
  );
}
